/* eslint-disable react/prop-types */
import React, { useState, useEffect } from "react";
import {
  Card,
  CardBody,
  Col,
  Container,
  Input,
  Label,
  Row,
  Button,
  Form,
  FormFeedback,
  Alert,
  Spinner,
} from "reactstrap";

//redux
import { useSelector, useDispatch } from "react-redux";
import ParticlesAuth from "./ParticlesAuth";
import { Link } from "react-router-dom";

// Formik validation
import * as Yup from "yup";
import { useFormik } from "formik";
import { enableLoading } from "../../slices/auth/login/reducer";
// thunks
import { loginUser } from "../../slices/thunks";

import logoLight from "../../assets/images/logo-light.png";

import withRouter from "../../Components/Common/withRouter";

const encodePassword = (value) => {
  if (!value) return "";
  try {
    return window.btoa(unescape(encodeURIComponent(value)));
  } catch (error) {
    console.error("Password encode error:", error);
    return "";
  }
};

const decodePassword = (value) => {
  if (!value) return "";

  // Check if it looks like base64 (contains only base64 characters and has proper length)
  const base64Pattern = /^[A-Za-z0-9+/=]+$/;
  const isBase64Like = base64Pattern.test(value) && value.length >= 4 && value.length % 4 === 0;

  // If it doesn't look like base64, it's probably plain text (old format)
  if (!isBase64Like) {
    return value;
  }

  // Try to decode base64
  try {
    const decoded = decodeURIComponent(escape(window.atob(value)));
    return decoded;
  } catch (error) {
    // If decoding fails, it might be old plain text password that happened to match pattern
    return value;
  }
};

const Login = (props) => {
  const [rememberMe, setRememberMe] = useState(false);
  const dispatch = useDispatch();

  const { errorMsg, loading } = useSelector((state) => ({
    errorMsg: state.Login.errorMsg,
    loading: state.Login.loading,
  }));

  const [passwordShow, setPasswordShow] = useState(false);

  // Get saved credentials from localStorage
  const getSavedPassword = () => {
    const saved = localStorage.getItem("rememberPassword");
    if (!saved) return "";
    return decodePassword(saved);
  };

  const savedEmail = localStorage.getItem("rememberEmail");
  const savedPassword = getSavedPassword();
  const savedRemember = localStorage.getItem("rememberMe") === "true";

  // Set rememberMe state if credentials are saved
  useEffect(() => {
    if (savedRemember && savedEmail) {
      setRememberMe(true);
    }
  }, []);

  useEffect(() => {
    const authUser = localStorage.getItem("authUser");

    if (authUser) {
      try {
        const obj = JSON.parse(authUser);
        const user = obj.user || obj;

        // Admin role validation
        if (
          obj.token &&
          user &&
          (user.role === "admin" || user.isAdmin === true)
        ) {
          props.router.navigate("/dashboard");
        } else {
          // If not admin, clear incorrect stored data
          localStorage.removeItem("authUser");
          localStorage.removeItem("expiryAt");
          localStorage.removeItem("expiryIn");
        }
      } catch (error) {
        // Invalid auth format, reset localStorage
        localStorage.removeItem("authUser");
        localStorage.removeItem("expiryAt");
        localStorage.removeItem("expiryIn");
      }
    }
  }, [props.router]);

  // Clear error message when component unmounts or user starts typing
  useEffect(() => {
    if (errorMsg) {
      // Error will be cleared automatically on successful login
    }
  }, [errorMsg]);

  const validation = useFormik({
    enableReinitialize: true,

    initialValues: {
      email: savedRemember && savedEmail ? savedEmail : "",
      password: savedRemember && savedPassword ? savedPassword : "",
    },

    validationSchema: Yup.object({
      email: Yup.string().required("Please Enter Your Email"),
      password: Yup.string().required("Please Enter Your Password"),
    }),

    onSubmit: (values) => {
      dispatch(enableLoading());

      if (rememberMe) {
        localStorage.setItem("rememberEmail", values.email);
        localStorage.setItem("rememberPassword", encodePassword(values.password));
        localStorage.setItem("rememberMe", "true");
      } else {
        localStorage.removeItem("rememberEmail");
        localStorage.removeItem("rememberPassword");
        localStorage.removeItem("rememberMe");
      }

      dispatch(loginUser(values, props.router));
    }
  });

  document.title = process.env.REACT_APP_SITE_TITLE + " | Login";
  return (
    <React.Fragment>
      <ParticlesAuth>
        <div className="auth-page-content">
          <Container>
            <Row className="mt-4">
              <Col lg={12}>
                <div className="text-center text-white-50">
                  <div className="mt-3">
                    <Link to="/dashboard" className="d-inline-block auth-logo">
                      <img src={logoLight} alt="" height="150" />
                    </Link>
                  </div>

                </div>
              </Col>
            </Row>

            <Row className="justify-content-center">
              <Col md={8} lg={6} xl={5}>
                <Card className="mt-4">
                  <CardBody className="p-4">
                    <div className="text-center mt-2">
                      <h5 className="text-primary">Welcome Back !</h5>
                      <p className="text-muted">
                        Sign in to continue to farmstayhub.
                      </p>
                    </div>
                    {errorMsg && errorMsg ? (
                      <Alert color="danger"> {errorMsg} </Alert>
                    ) : null}
                    <div className="p-2 mt-4">
                      <Form
                        onSubmit={(e) => {
                          e.preventDefault();
                          validation.handleSubmit();
                          return false;
                        }}
                        action="#"
                      >
                        <div className="mb-3">
                          <Label htmlFor="email" className="form-label">
                            Email
                          </Label>
                          <Input
                            name="email"
                            className="form-control"
                            placeholder="Enter email"
                            type="email"
                            onChange={validation.handleChange}
                            onBlur={validation.handleBlur}
                            value={validation.values.email || ""}
                            invalid={
                              validation.touched.email &&
                                validation.errors.email
                                ? true
                                : false
                            }
                          />
                          {validation.touched.email &&
                            validation.errors.email ? (
                            <FormFeedback type="invalid">
                              {validation.errors.email}
                            </FormFeedback>
                          ) : null}
                        </div>

                        <div className="mb-3">
                          <div className="float-end">
                            <Link to="/forgot-password" className="text-muted">
                              Forgot password?
                            </Link>
                          </div>
                          <Label
                            className="form-label"
                            htmlFor="password-input"
                          >
                            Password
                          </Label>
                          <div className="position-relative auth-pass-inputgroup mb-3">
                            <Input
                              name="password"
                              value={validation.values.password || ""}
                              type={passwordShow ? "text" : "password"}
                              className="form-control pe-5"
                              placeholder="Enter Password"
                              onChange={validation.handleChange}
                              onBlur={validation.handleBlur}
                              invalid={
                                validation.touched.password &&
                                  validation.errors.password
                                  ? true
                                  : false
                              }
                            />
                            {validation.touched.password &&
                              validation.errors.password ? (
                              <FormFeedback type="invalid">
                                {validation.errors.password}
                              </FormFeedback>
                            ) : null}
                            <button
                              className="btn btn-link position-absolute end-0 top-0 text-decoration-none text-muted"
                              type="button"
                              id="password-addon"
                              onClick={() => setPasswordShow(!passwordShow)}
                            >
                              {" "}
                              <i className="ri-eye-fill align-middle"></i>{" "}
                            </button>
                          </div>
                        </div>

                        <div className="form-check">
                          <Input
                            className="form-check-input"
                            type="checkbox"
                            checked={rememberMe}
                            onChange={(e) => {
                              const checked = e.target.checked;
                              setRememberMe(checked);
                              if (!checked) {
                                // Clear saved password when unchecked
                                localStorage.removeItem("rememberPassword");
                                validation.setFieldValue("password", "");
                              }
                            }}
                            id="auth-remember-check"
                          />
                          <Label className="form-check-label" htmlFor="auth-remember-check">
                            Remember me
                          </Label>
                        </div>

                        <div className="mt-4">
                          <Button
                            disabled={loading ? true : false}
                            color="primary"
                            className="btn btn-primary w-100"
                            type="submit"
                          >
                            {loading === true ? (
                              <>
                                <Spinner size="sm" className="me-2" />
                                Loading...
                              </>
                            ) : "Sign In"}
                          </Button>
                        </div>
                      </Form>
                    </div>
                  </CardBody>
                </Card>
              </Col>
            </Row>
          </Container>
        </div>
      </ParticlesAuth>
    </React.Fragment>
  );
};

export default withRouter(Login);
